import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Client details & KYC — applicant, IDs, addresses, joint/family, nominee, status.
const STATUS = [['pending', 'Pending'], ['submitted', 'Documents submitted'], ['verified', 'Verified'], ['rejected', 'Rejected']];

const ClientKyc = ({ apiBase, clientId }) => {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${apiBase}/client-docs/${clientId}/kyc`)
      .then((r) => setD({ ...r.data, date_of_birth: r.data.date_of_birth ? String(r.data.date_of_birth).slice(0, 10) : '' }))
      .catch((e) => setError(e.response?.data?.error || 'Could not load KYC'));
  }, [apiBase, clientId]);

  if (!d) return error ? <div className="card" style={{ marginBottom: '16px', color: '#b91c1c' }}>{error}</div> : null;

  const set = (k) => (e) => setD({ ...d, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await axios.put(`${apiBase}/client-docs/${clientId}/kyc`, d);
      setMsg('KYC saved'); setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Your role is not allowed to edit KYC.' : (err.response?.data?.error || 'Could not save'));
    } finally { setSaving(false); }
  };

  const field = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '4px' };
  const cell = { flex: '1 1 200px', minWidth: 0 };
  const wide = { flex: '1 1 100%' };
  const head = { flex: '1 1 100%', fontSize: '13px', fontWeight: 700, color: '#111827', marginTop: '8px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' };
  const I = (k, label, props = {}) => (
    <div style={props.wide ? wide : cell}>
      <label style={lbl}>{label}</label>
      <input style={field} value={d[k] ?? ''} onChange={set(k)} {...props.input} />
    </div>
  );
  const statusColor = { verified: '#059669', submitted: '#2563eb', rejected: '#dc2626' }[d.kyc_status] || '#b45309';

  return (
    <form className="card" style={{ marginBottom: '16px' }} onSubmit={save}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="card-title" style={{ margin: 0 }}>Client details & KYC</h2>
        <span style={{ fontSize: '12px', fontWeight: 700, color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '999px', padding: '3px 10px' }}>
          {(STATUS.find((s) => s[0] === d.kyc_status) || STATUS[0])[1]}
        </span>
      </div>
      {d.masked && <p style={{ fontSize: '12px', color: '#6b7280' }}>PAN and Aadhaar are partly hidden for your role; leave them as they are to keep the saved numbers.</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '10px' }}>
        <div style={head}>Applicant</div>
        {I('name', 'Full name (as on PAN) *')}
        {I('date_of_birth', 'Date of birth', { input: { type: 'date' } })}
        {I('pan_number', 'PAN', { input: { placeholder: 'ABCDE1234F', style: { ...field, textTransform: 'uppercase' } } })}
        {I('aadhaar_number', 'Aadhaar', { input: { placeholder: '12 digits', inputMode: 'numeric' } })}
        {I('occupation', 'Occupation')}
        {I('company_name', 'Company / employer')}
        <div style={cell}>
          <label style={lbl}>Company type</label>
          <select style={field} value={d.company_type || ''} onChange={set('company_type')}>
            <option value="">—</option>
            {['Salaried', 'Self-employed', 'Business owner', 'Professional', 'Retired', 'Homemaker', 'Other'].map((x) => <option key={x}>{x}</option>)}
          </select>
        </div>

        <div style={head}>Contact</div>
        <div style={cell}><label style={lbl}>Mobile</label><input style={{ ...field, background: '#f9fafb' }} value={d.phone || ''} readOnly title="Change the mobile from the Clients page" /></div>
        {I('alternate_phone', 'Alternate mobile', { input: { inputMode: 'tel' } })}
        {I('email', 'Email', { input: { type: 'email' } })}
        {I('secondary_email', 'Second email', { input: { type: 'email' } })}

        <div style={head}>Addresses</div>
        {I('address', 'Residential address', { wide: true })}
        {I('city', 'City')}
        <div style={{ ...cell, display: 'flex', alignItems: 'flex-end', gap: '8px', fontSize: '14px', paddingBottom: '8px' }}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="checkbox" checked={!!d.is_nri} onChange={set('is_nri')} /> NRI</label>
        </div>
        {d.is_nri && I('country', 'Country of residence')}
        {I('office_address', 'Office / correspondence address', { wide: true })}

        <div style={head}>Joint applicants, family & nominee</div>
        {I('additional_names', 'Co-applicant name(s)', { wide: true, input: { placeholder: 'e.g. Sameera Shaikh (wife), Arif Shaikh (son)' } })}
        {I('spouse_name', 'Spouse name')}
        {I('secondary_phone', 'Co-applicant / family mobile', { input: { inputMode: 'tel' } })}
        {I('nominee_name', 'Nominee name')}
        {I('nominee_relation', 'Nominee relation')}

        <div style={head}>KYC status</div>
        <div style={cell}>
          <select style={field} value={d.kyc_status} onChange={set('kyc_status')}>
            {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {error && <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', color: '#b91c1c', borderRadius: '6px', fontSize: '13px' }}>{error}</div>}
      {msg && <div style={{ marginTop: '12px', padding: '10px 14px', background: '#ecfdf5', color: '#065f46', borderRadius: '6px', fontSize: '13px' }}>{msg}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save details & KYC'}</button>
      </div>
    </form>
  );
};

export default ClientKyc;
